import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import { useProfile } from '@/api/profile'
import { useCompanyPhoneNumbers, type CompanyPhoneNumber } from '@/api/company'
import { usePortRequests, type PortRequest } from '@/api/portage'
import {
  useAssignNumberToFlow,
  useCreateIvrFlow,
  useIvrFlowDetail,
} from '@/api/ivrFlows'
import { NumbersListView } from './numbers/NumbersListView'
import { PhoneNumberDetailView } from './numbers/PhoneNumberDetailView'
import { PortRequestDetailView } from './numbers/portage/PortRequestDetailView'
import { PortWizardDialog } from './numbers/portage/PortWizardDialog'
import { IvrFlowEditor } from './flows/editor/IvrFlowEditor'
import { isPhoneOrTablet } from '@/lib/device'
import { formatHeure } from '@/lib/heure'

export function NumbersView() {
  const { t, i18n } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data } = useCompanyPhoneNumbers(companyId)

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [editingFlowId, setEditingFlowId] = useState<number | null>(null)
  // A transfer's page lives in the address (?transfer=…): the emails link to it.
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTransferId = searchParams.get('transfer')
  const { data: ports } = usePortRequests(companyId)
  const [wizard, setWizard] = useState<{ open: boolean; transfer: PortRequest | null }>({ open: false, transfer: null })
  const ownerEmail = profile?.email ?? ''
  const { locale = 'fr' } = useParams<{ locale: string }>()
  // On a phone, transfers have their own page at the real phone width (pages/Transfer.tsx),
  // reached by a real page load so that the dashboard's 1280 px layout does not follow.
  const [phone] = useState(isPhoneOrTablet)
  const openTransferPage = (id?: string) => window.location.assign(`/${locale}/transfert${id ? `/${id}` : ''}`)
  const toTransferPage = phone && !!selectedTransferId

  useEffect(() => {
    if (toTransferPage && selectedTransferId) window.location.replace(`/${locale}/transfert/${selectedTransferId}`)
  }, [toTransferPage, selectedTransferId, locale])

  const selectTransfer = (id: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (id) next.set('transfer', id)
    else next.delete('transfer')
    setSearchParams(next, { replace: !id })
  }

  const { data: editingFlow, isLoading: flowLoading } =
    useIvrFlowDetail(editingFlowId)
  const createFlow = useCreateIvrFlow()
  const assignNumberToFlow = useAssignNumberToFlow()

  const selectedNumber =
    data?.phone_numbers.find((n) => n.id === selectedId) ?? null

  // Si le numéro sélectionné a disparu (delete), retomber sur la liste.
  useEffect(() => {
    if (selectedId !== null && data && !selectedNumber) {
      setSelectedId(null)
    }
  }, [selectedId, data, selectedNumber])

  if (!companyId || toTransferPage) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  // Mode éditeur fullscreen (priorité sur le détail/liste).
  if (editingFlowId !== null) {
    if (flowLoading || !editingFlow) {
      return (
        <div className="h-full flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )
    }
    return (
      <IvrFlowEditor
        flow={editingFlow}
        onBack={() => setEditingFlowId(null)}
      />
    )
  }

  // Création d'un flow vide assigné au numéro courant, puis ouverture de l'éditeur.
  // Le backend rejette les noms en doublon (par company) → on construit un nom
  // unique à partir du numéro (lui-même unique), avec timestamp en filet de
  // sécurité si l'user recrée plusieurs flows sur le même numéro.
  const handleCreateFlowForNumber = async (numberId: number) => {
    const number = data?.phone_numbers.find((n) => n.id === numberId)
    const timestamp = formatHeure(new Date(), i18n.language)
    const baseName = t('manager.flows.defaultName', 'Nouveau flux')
    const name = number?.phone_number
      ? `${baseName} ${number.phone_number} (${timestamp})`
      : `${baseName} (${timestamp})`
    try {
      const newFlow = await createFlow.mutateAsync({ name })
      await assignNumberToFlow.mutateAsync({
        flowId: newFlow.id,
        phoneNumberId: numberId,
      })
      setEditingFlowId(newFlow.id)
    } catch {
      toast.error(t('manager.flows.errorCreate'))
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          {selectedTransferId ? (
            <PortRequestDetailView
              companyId={companyId}
              transferId={selectedTransferId}
              onBack={() => selectTransfer(null)}
              onResume={(transfer) => setWizard({ open: true, transfer })}
            />
          ) : selectedNumber ? (
            <PhoneNumberDetailView
              number={selectedNumber}
              companyId={companyId}
              onBack={() => setSelectedId(null)}
              onDeleted={() => setSelectedId(null)}
              onEditFlow={(flowId) => setEditingFlowId(flowId)}
              onCreateFlow={() => void handleCreateFlowForNumber(selectedNumber.id)}
            />
          ) : (
            <NumbersListView
              companyId={companyId}
              onSelect={(num: CompanyPhoneNumber) => setSelectedId(num.id)}
              onSelectTransfer={(transfer) =>
                phone
                  ? openTransferPage(transfer.id)
                  : // A draft opens straight in the wizard; the others on their page.
                    transfer.status === 'brouillon'
                    ? setWizard({ open: true, transfer })
                    : selectTransfer(transfer.id)
              }
              onStartTransfer={() => (phone ? openTransferPage() : setWizard({ open: true, transfer: null }))}
            />
          )}
        </div>
      </div>
      <PortWizardDialog
        open={wizard.open}
        onClose={() => setWizard({ open: false, transfer: null })}
        companyId={companyId}
        transfer={wizard.transfer}
        options={ports?.options}
        ownerEmail={ownerEmail}
        onSubmitted={(id) => {
          setWizard({ open: false, transfer: null })
          selectTransfer(id)
        }}
      />
    </div>
  )
}
